// CollectionGrid.jsx - Virtualized grid component for displaying collection games
import React, { useState, useCallback, useRef, useEffect } from 'react';
import CollectionTileWithBulkOnline from '../common/CollectionTileWithBulkOnline';
import logger from '../../utils/logger';

function CollectionGrid({
  games = [],
  loading = false,
  hasMore = false,
  onLoadMore,
  onGameClick,
  onAddToCollection,
  onPreviewGame,
  emptyMessage = 'No games found',
  emptySubtext = 'Try adjusting your filters or add some games to your collection.',
  itemsPerLoad = 20,
}) {
  const [displayedItems, setDisplayedItems] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const observerRef = useRef(null);
  const loadingRef = useRef(false);

  // Initialize displayed items
  useEffect(() => {
    setDisplayedItems(games.slice(0, itemsPerLoad));
  }, [games, itemsPerLoad]);

  // Infinite scroll observer
  const loadMoreItems = useCallback(() => {
    if (loadingRef.current || isLoadingMore || !hasMore) {
      return;
    }

    loadingRef.current = true;
    setIsLoadingMore(true);

    // Simulate loading delay for better UX
    setTimeout(() => {
      const currentLength = displayedItems.length;
      const nextItems = games.slice(currentLength, currentLength + itemsPerLoad);

      if (nextItems.length === 0) {
        setIsLoadingMore(false);
        loadingRef.current = false;
        return;
      }

      setDisplayedItems(prev => [...prev, ...nextItems]);
      setIsLoadingMore(false);
      loadingRef.current = false;

      if (onLoadMore) {
        onLoadMore();
      }
    }, 300);
  }, [displayedItems, games, hasMore, isLoadingMore, itemsPerLoad, onLoadMore]);

  // Set up intersection observer for infinite scroll
  useEffect(() => {
    if (!observerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && hasMore && !isLoadingMore) {
          logger.debug('Loading more collection items', {
            currentCount: displayedItems.length,
            totalCount: games.length,
          });
          loadMoreItems();
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1,
      }
    );

    const target = observerRef.current;
    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
      observer.disconnect();
    };
  }, [hasMore, isLoadingMore, loadMoreItems, displayedItems.length, games.length]);

  // Handle game interactions
  const handleGameClick = useCallback((game) => {
    logger.debug('Game clicked', { gameId: game.id, gameName: game.name });
    if (onGameClick) {
      onGameClick(game);
    }
  }, [onGameClick]);

  const handleAddToCollection = useCallback((game) => {
    logger.debug('Add to collection clicked', { gameId: game.id, gameName: game.name });
    if (onAddToCollection) {
      onAddToCollection(game);
    }
  }, [onAddToCollection]);

  const handlePreviewGame = useCallback((game) => {
    logger.debug('Preview game clicked', { gameId: game.id, gameName: game.name });
    if (onPreviewGame) {
      onPreviewGame(game);
    }
  }, [onPreviewGame]);

  // Reset displayed items when games change significantly
  useEffect(() => {
    if (Math.abs(games.length - displayedItems.length) > itemsPerLoad) {
      setDisplayedItems(games.slice(0, itemsPerLoad));
    }
  }, [games, games.length, displayedItems.length, itemsPerLoad, setDisplayedItems]);

  if (loading && displayedItems.length === 0) {
    return (
      <div className="collection-grid-loading" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '200px',
        flexDirection: 'column',
        gap: '1rem',
      }}>
        <div className="loading-spinner" style={{
          width: '40px',
          height: '40px',
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #1976d2',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }}></div>
        <p style={{ color: 'var(--text-secondary, #666)', margin: 0 }}>
          Loading your collection...
        </p>
      </div>
    );
  }

  if (games.length === 0 && !loading) {
    return (
      <div className="collection-empty" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        textAlign: 'center',
        padding: '2rem',
      }}>
        <div style={{
          fontSize: '3rem',
          marginBottom: '1rem',
          opacity: 0.3,
        }}>
          🎮
        </div>
        <h3 style={{
          margin: '0 0 0.5rem 0',
          color: 'var(--text-primary, #333)',
          fontSize: '1.25rem',
        }}>
          {emptyMessage}
        </h3>
        <p style={{
          margin: '0',
          color: 'var(--text-secondary, #666)',
          fontSize: '0.9rem',
          lineHeight: '1.4',
        }}>
          {emptySubtext}
        </p>
      </div>
    );
  }

  return (
    <div className="collection-grid">
      {displayedItems.map((game) => (
        <CollectionTileWithBulkOnline
          key={game.id || game.game_id}
          game={game}
          onClick={handleGameClick}
          onAddToCollection={handleAddToCollection}
          onPreview={handlePreviewGame}
          style={{
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-4px)';
            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
          }}
        />
      ))}

      {/* Loading indicator for infinite scroll */}
      {hasMore && (
        <div
          ref={observerRef}
          className="load-more-indicator"
          style={{
            gridColumn: '1 / -1',
            display: 'flex',
            justifyContent: 'center',
            padding: '2rem',
          }}
        >
          {isLoadingMore ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div className="loading-spinner-small" style={{
                width: '20px',
                height: '20px',
                border: '2px solid #f3f3f3',
                borderTop: '2px solid #1976d2',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
              }}></div>
              <span style={{ color: 'var(--text-secondary, #666)' }}>
                Loading more games...
              </span>
            </div>
          ) : (
            <div style={{
              color: 'var(--text-secondary, #666)',
              fontSize: '0.9rem',
              textAlign: 'center',
            }}>
              Scroll for more games
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default CollectionGrid;
